import { AxiosResponse } from "axios";
import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";
import * as actions from "./actions";
import * as actionTypes from "./actionTypes";
import * as types from "./types";
import * as services from "./service";
import * as alert from "../alert/actions";

function* postSpokeSaga({ payload }: types.PostSpokeRequest) {
  const timestamp = new Date().toLocaleString();
  try {
    const response: AxiosResponse<types.IPostSpokeSuccess | types.IApiError> =
      yield call(services.postSpoke, payload);

    if (response.status === 201) {
      let dataPayload = response.data as types.IPostSpokeSuccess;
      yield put(
        actions.postSpokeSuccess({
          spoke: dataPayload,
        })
      );
      let successMsg = `The spoke was successfully created: ${dataPayload.name}, spoke ID: ${dataPayload.spokeId}. Timestamp: ${timestamp}`;
      yield put(
        alert.enqueueSnackbar({
          message: successMsg,
          options: { variant: "success" },
        })
      );
    }

    if (response.status !== 201) {
      let apiError = response.data as types.IApiError;
      let errMsg = `StatusCode: ${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}. Timestamp: ${timestamp}`;
      yield put(
        alert.enqueueSnackbar({
          message: errMsg,
          options: { variant: "error" },
        })
      );
    }
  } catch (e: any) {
    yield put(
      actions.postSpokeFailure({
        error: e,
      })
    );
  }
}

function* deleteSpokeSaga(action: types.DeleteSpokeRequest) {
  const timestamp = new Date().toLocaleString();
  try {
    const response: AxiosResponse<types.IDeleteSpokeSuccess | types.IApiError> = yield call(services.deleteSpoke, action.params);

    if (response.status === 200) {
      yield put(
        actions.deleteSpokeSuccess({
          isSuccess: true,
          errorMessage: ""
        })
      );
      yield put(alert.enqueueSnackbar({ message: `The spoke was successfully deleted. Timestamp: ${timestamp}`, options: { variant: "success" } }));

    }

    //Show IApiError
    if (response.status !== 200) {
      let apiError = response.data as types.IApiError;
      let errmsg = `StatusCode:${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}. Timestamp: ${timestamp}`;
      yield put(alert.enqueueSnackbar({ message: errmsg, options: { variant: "error" } }));
    }

  } catch (e: any) {
    yield put(
      actions.deleteSpokeFailure({
        error: e,
      })
    );
  }
}

/*
  Watcher saga - intercept the action fired from view
  Starts worker saga on latest dispatched `FETCH_ACCESSCONTROL_REQUEST` action.
  Allows concurrent increments.
*/
function* postDataSpokeSaga() {
  yield takeLatest(actionTypes.POST_SPOKE_REQUEST, postSpokeSaga);
}

//List Spokes
function* fetchSpokesListSaga(action: types.FetchSpokesReq) {
  try {
    const response: AxiosResponse<types.ISpokeDetails[]> = yield call(
      services.getSpokeDetails,
      action.params
    );

    yield put(
      actions.fetchSpokesSuccess({
        spokes: response.data as types.ISpokeDetails[],
      })
    );
  } catch (e: any) {
    yield put(
      alert.enqueueSnackbar({
        message: `Could not load spokes. Timestamp: ${new Date().toLocaleString()}`,
        options: { variant: "error" },
      })
    );
  }
}

function* fetchSpokesListSagaPageLoad(action: types.FetchSpokesListReq) {
  const timestamp = new Date().toLocaleString();
  try {
    const response: AxiosResponse<types.ISpokeListDetails[]> = yield call(
      services.getSpokeListDetails,
      action.params
    );

    yield put(
      actions.fetchSpokesListSuccess({
        spokesList: response.data as types.ISpokeListDetails[],
      })
    );
  } catch (e: any) {
    yield put(
      alert.enqueueSnackbar({
        message: `Could not load spokes. Timestamp: ${timestamp}`,
        options: { variant: "error" },
      })
    );
  }
}

//Spoke List By Id
function* fetchSpokesListByIdSaga(action: types.FetchSpokesByIdReq) {
  try {
    const response: AxiosResponse<types.ISpokeDetails> = yield call(
      services.getSpokeDetailsById,
      action.params
    );

    yield put(
      actions.fetchSpokebyIdSuccess({
        spoke: response.data,
      })
    );
  } catch (e: any) {
    yield put(
      alert.enqueueSnackbar({
        message: `Could not load spokes. Timestamp: ${new Date().toLocaleString()}`,
        options: { variant: "error" },
      })
    );
  }
}

function* fetchSpokesSaga() {
  yield takeLatest(actionTypes.FETCH_SPOKE_LIST_REQUEST, fetchSpokesListSaga);
}

function* fetchSpokesListDetailsSaga() {
  yield takeLatest(actionTypes.FETCH_SPOKE_LIST_LOAD_REQUEST, fetchSpokesListSagaPageLoad);
}

function* delSpokeSaga() {
  yield takeEvery(actionTypes.DELETE_SPOKE_REQUEST, deleteSpokeSaga);
}

function* fetchSpokeByIdSaga() {
  yield takeLatest(
    actionTypes.FETCH_SPOKE_BY_ID_REQUEST,
    fetchSpokesListByIdSaga
  );
}

function* spokeSaga() {
  yield all([postDataSpokeSaga(), fetchSpokesSaga(), fetchSpokeByIdSaga(), delSpokeSaga(), fetchSpokesListDetailsSaga()]);
}

export default spokeSaga;
